import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import {
  showBrowserNotification,
  getNotifyPref,
  notifyPermission,
  isPushSupported,
  hasPushSubscription,
  enablePushSubscription,
} from '../lib/browserNotifications';
import { pushApi } from '../api/push';

/**
 * Invisible listener that turns company-room socket events into browser
 * pop-up notifications (new tickets, replies, status changes, SLA breaches).
 * Mounted once in the staff AppLayout. Your own actions never notify you.
 */
export default function BrowserNotifyListener() {
  const { socket, isConnected } = useSocket();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const myId = (user as any)?.id ?? (user as any)?._id;

  // Draft presence: someone started (or dropped) an unsent draft. Not a
  // notification — just refresh whatever lists are open so their draft
  // markers are current. Your own drafts don't need the round trip.
  useEffect(() => {
    if (!socket) return;
    const onDraft = (p: any) => {
      if (!p?.ticketId || p.userId === myId) return;
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['dash-list'] });
      queryClient.invalidateQueries({ queryKey: ['ticket', p.ticketId] });
    };
    // Sub-ticket tasks / collaborators changed somewhere: refresh the ticket,
    // its task views, and every list carrying task marks. A task handed to
    // you by someone else also gets a pop-up — it's your work now.
    const onTasks = (p: any) => {
      if (!p?.ticketId) return;
      queryClient.invalidateQueries({ queryKey: ['ticket', p.ticketId] });
      queryClient.invalidateQueries({ queryKey: ['ticket-task', p.ticketId] });
      queryClient.invalidateQueries({ queryKey: ['ticket-activity', p.ticketId] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['dash-list'] });
      if ((p.kind === 'assigned' || p.kind === 'added') && p.assignedToId === myId && p.actorId !== myId) {
        showBrowserNotification({
          title: 'A task is now yours',
          body: p.title,
          url: `/tickets/${p.ticketId}`,
          tag: `ticket-task-${p.taskId ?? p.ticketId}`,
        });
      }
    };
    const onCollaborators = (p: any) => {
      if (!p?.ticketId) return;
      queryClient.invalidateQueries({ queryKey: ['ticket', p.ticketId] });
      queryClient.invalidateQueries({ queryKey: ['ticket-activity', p.ticketId] });
    };
    // CRM inbox: any change refreshes the inbox lists and open threads.
    const onCrm = () => {
      queryClient.invalidateQueries({ queryKey: ['crm-conversations'] });
      queryClient.invalidateQueries({ queryKey: ['crm-conversation'] });
      queryClient.invalidateQueries({ queryKey: ['crm-stats'] });
    };
    socket.on('crm:conversation', onCrm);
    socket.on('ticket:draft', onDraft);
    socket.on('ticket:tasks', onTasks);
    socket.on('ticket:collaborators', onCollaborators);
    return () => {
      socket.off('crm:conversation', onCrm);
      socket.off('ticket:draft', onDraft);
      socket.off('ticket:tasks', onTasks);
      socket.off('ticket:collaborators', onCollaborators);
    };
  }, [socket, isConnected, myId, queryClient]);

  // Users who enabled notifications get a Web Push subscription for this
  // browser automatically (covers pre-push enablers and re-installs) so
  // delivery keeps working when the app is closed.
  useEffect(() => {
    if (!myId || !isPushSupported()) return;
    if (!getNotifyPref() || notifyPermission() !== 'granted') return;
    hasPushSubscription()
      .then(subscribed => {
        if (subscribed) return;
        return enablePushSubscription(
          async () => (await pushApi.publicKey()).publicKey,
          (sub) => pushApi.subscribe(sub),
        ).then(() => undefined);
      })
      .catch(() => undefined);
  }, [myId]);

  useEffect(() => {
    if (!socket) return;

    const onCreated = ({ ticket }: any) => {
      if (!ticket || ticket.createdById === myId) return;
      showBrowserNotification({
        title: `New ticket ${ticket.ticketNumber ?? ''}`.trim(),
        body: ticket.title,
        url: `/tickets/${ticket.id}`,
        tag: `ticket-created-${ticket.id}`,
      });
    };

    const onStatusChanged = (p: any) => {
      if (!p || p.actorId === myId) return;
      showBrowserNotification({
        title: `${p.ticketNumber ?? 'Ticket'} status changed`,
        body: p.statusLabel ? `Now: ${p.statusLabel}` : undefined,
        url: `/tickets/${p.ticketId}`,
        tag: `ticket-status-${p.ticketId}`,
      });
    };

    const onMessage = ({ ticketId, message }: any) => {
      if (!message) return;
      const authorId = message.author?.id ?? message.authorId;
      if (authorId === myId) return;
      const isNote = message.type === 'internal_note';
      const isTaskNote = message.type === 'task_note';
      showBrowserNotification({
        title: isNote ? 'New internal note' : isTaskNote ? 'New task note' : 'New ticket reply',
        body: (message.authorName || message.author?.name) ? `From ${message.authorName || message.author.name}` : undefined,
        url: `/tickets/${ticketId ?? message.ticketId}`,
        tag: `ticket-msg-${ticketId ?? message.ticketId}`,
      });
    };

    const onSlaBreached = ({ type, tickets }: any) => {
      const count = Array.isArray(tickets) ? tickets.length : 0;
      if (!count) return;
      showBrowserNotification({
        title: `SLA ${type === 'response' ? 'response' : 'resolution'} breach`,
        body: `${count} ticket${count === 1 ? '' : 's'} breached`,
        url: '/tickets',
        tag: `sla-${type}`,
      });
    };

    socket.on('ticket:created', onCreated);
    socket.on('ticket:statusChanged', onStatusChanged);
    socket.on('ticket:message', onMessage);
    const onCrmInbound = (p: any) => {
      if (!p || p.kind !== 'inbound') return;
      showBrowserNotification({
        title: p.isNew ? 'New CRM inbox message' : `Reply on CRM-${p.number}`,
        url: `/crm/inbox/${p.conversationId}`,
        tag: `crm-${p.conversationId}`,
      });
    };
    socket.on('crm:conversation', onCrmInbound);
    socket.on('sla:breached', onSlaBreached);
    return () => {
      socket.off('ticket:created', onCreated);
      socket.off('ticket:statusChanged', onStatusChanged);
      socket.off('ticket:message', onMessage);
      socket.off('crm:conversation', onCrmInbound);
      socket.off('sla:breached', onSlaBreached);
    };
  }, [socket, isConnected, myId]);

  return null;
}
